import { formatMoney } from '@mt/shared';
import * as D from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import { FileText, Package, Search, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Kbd } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/states';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { Customer, Paged, Product, QuoteHeader } from '@/lib/types';
import { cn, fmtDate } from '@/lib/utils';
import { isCtvOnly } from '@/lib/roles';

interface Hit {
  key: string;
  kind: 'product' | 'customer' | 'quote';
  title: string;
  sub: string;
  right?: string;
  go: string;
}

const KIND = {
  product: { label: 'Sản phẩm', icon: Package },
  customer: { label: 'Khách hàng', icon: Users },
  quote: { label: 'Báo giá', icon: FileText },
} as const;

/** Tìm nhanh sản phẩm, khách hàng, báo giá từ mọi trang (Ctrl+K). Chỉ tìm trong phần người dùng có quyền xem. */
export function GlobalSearch({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const nav = useNavigate();
  const { can, user } = useAuth();
  // DEC-375: CTV chỉ thấy tên sản phẩm (không mã, không giá)
  const ctv = isCtvOnly(user?.roles);
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [active, setActive] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    if (!open) setQ('');
  }, [open]);

  const enabled = open && debounced.length >= 2;
  const products = useQuery({
    queryKey: ['global-search', 'products', debounced],
    queryFn: () => api.get<Paged<Product>>('/products', { q: debounced, active: true, pageSize: 6 }),
    enabled: enabled && can('product.read'),
  });
  const customers = useQuery({
    queryKey: ['global-search', 'customers', debounced],
    queryFn: () => api.get<Paged<Customer>>('/customers', { q: debounced, pageSize: 5 }),
    enabled: enabled && can('customer.read'),
  });
  const quotes = useQuery({
    queryKey: ['global-search', 'quotes', debounced],
    queryFn: () => api.get<Paged<QuoteHeader>>('/quotes', { q: debounced, pageSize: 5 }),
    enabled: enabled && can('quote.read'),
  });

  const hits = useMemo<Hit[]>(() => {
    if (!enabled) return [];
    return [
      ...(products.data?.items ?? []).map((p) => ({
        key: `p${p.id}`,
        kind: 'product' as const,
        title: ctv ? p.name : `${p.code} – ${p.name}`,
        sub: [p.spec, p.groupName, p.brandName].filter(Boolean).join(' · '),
        right: !ctv && p.salePrice ? `${formatMoney(p.salePrice)} đ/${p.unit}` : p.unit,
        go: `/san-pham?q=${encodeURIComponent(ctv ? p.name : p.code)}`,
      })),
      ...(customers.data?.items ?? []).map((c) => ({
        key: `c${c.id}`,
        kind: 'customer' as const,
        title: c.name,
        sub: [c.code, c.taxCode && `MST ${c.taxCode}`, c.phone].filter(Boolean).join(' · '),
        right: c.ownerName ?? undefined,
        go: `/khach-hang/${c.id}`,
      })),
      ...(quotes.data?.items ?? []).map((x) => ({
        key: `q${x.id}`,
        kind: 'quote' as const,
        title: `${x.code} – ${x.customerName}`,
        sub: `${fmtDate(x.quoteDate)} · ${x.salesName}`,
        right: `${formatMoney(x.grandTotal)} đ`,
        go: `/bao-gia/${x.id}`,
      })),
    ];
  }, [enabled, products.data, customers.data, quotes.data, ctv]);

  useEffect(() => setActive(0), [hits]);
  const loading = enabled && (products.isFetching || customers.isFetching || quotes.isFetching);

  const go = (h?: Hit) => {
    if (!h) return;
    onOpenChange(false);
    nav(h.go);
  };

  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-40 bg-overlay" />
        <D.Content className="fixed top-[12vh] left-1/2 z-50 w-[94vw] max-w-2xl -translate-x-1/2 overflow-hidden rounded-xl bg-card shadow-2xl outline-none">
          <D.Title className="sr-only">Tìm kiếm nhanh</D.Title>
          <D.Description className="sr-only">Tìm sản phẩm, khách hàng, báo giá</D.Description>
          <div className="flex items-center gap-2 border-b px-4">
            <Search className="size-5 text-muted-foreground" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setActive((a) => Math.min(hits.length - 1, a + 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setActive((a) => Math.max(0, a - 1));
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  go(hits[active]);
                }
              }}
              placeholder="Tìm sản phẩm, mã hàng, khách hàng, số báo giá..."
              className="h-14 flex-1 bg-transparent text-base outline-none"
            />
            {loading && <Spinner className="text-muted-foreground" />}
            <Kbd>Esc</Kbd>
          </div>
          <div className="max-h-[60vh] overflow-auto py-1">
            {!enabled ? (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">Gõ ít nhất 2 ký tự. Tìm không dấu được: “ong thep dn65”.</p>
            ) : !hits.length && !loading ? (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">Không tìm thấy kết quả.</p>
            ) : (
              hits.map((h, i) => {
                const K = KIND[h.kind];
                const first = i === 0 || hits[i - 1]!.kind !== h.kind;
                return (
                  <div key={h.key}>
                    {first && <div className="px-4 pt-2 pb-1 text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">{K.label}</div>}
                    <button
                      onMouseEnter={() => setActive(i)}
                      onClick={() => go(h)}
                      className={cn('flex w-full items-center gap-3 px-4 py-2 text-left', i === active && 'bg-accent')}
                    >
                      <K.icon className="size-4 shrink-0 text-primary" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">{h.title}</span>
                        {h.sub && <span className="block truncate text-xs text-muted-foreground">{h.sub}</span>}
                      </span>
                      {h.right && <span className="shrink-0 text-xs text-muted-foreground tabular-nums">{h.right}</span>}
                    </button>
                  </div>
                );
              })
            )}
          </div>
          <div className="flex gap-3 border-t bg-muted/50 px-4 py-2 text-[11px] text-muted-foreground">
            <span><Kbd>↑↓</Kbd> chọn</span>
            <span><Kbd>Enter</Kbd> mở</span>
            <span><Kbd>Ctrl+K</Kbd> mở tìm kiếm ở mọi trang</span>
          </div>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
